import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Icons } from "../../assets/images/icons";

interface CalendarProps {
  value?: Date;
  onValueChange?: (date: Date) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  disabledDates?: (date: Date) => boolean;
}

export const Calendar: React.FC<CalendarProps> = ({
  value,
  onValueChange,
  placeholder = "Select date",
  disabled = false,
  className = "",
  disabledDates,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<"bottom" | "top">("bottom");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const calendarRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const calculatePosition = () => {
    if (!triggerRef.current) return "bottom";

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const calendarHeight = 320; // Approximate calendar height

    // If there's not enough space below but enough space above, open upwards
    if (spaceBelow < calendarHeight && spaceAbove >= calendarHeight) {
      return "top";
    }

    return "bottom";
  };

  const handleToggle = () => {
    if (disabled) return;

    if (!isOpen) {
      // Calculate position before opening
      setPosition(calculatePosition());
    }
    setIsOpen(!isOpen);
  };

  // Get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  // Navigate to previous/next month
  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(
        direction === "prev" ? prev.getMonth() - 1 : prev.getMonth() + 1
      );
      return newDate;
    });
  };

  // Handle date selection
  const handleDateSelect = (day: number) => {
    const selectedDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    onValueChange?.(selectedDate);
    setIsOpen(false);
  };

  // Format date for display
  const formatDisplayDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8" />);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isSelected =
        value &&
        value.getDate() === day &&
        value.getMonth() === month &&
        value.getFullYear() === year;

      const isToday = new Date().toDateString() === date.toDateString();

      // Check if date should be disabled
      const isDisabled = disabledDates ? disabledDates(date) : false;

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateSelect(day)}
          disabled={isDisabled}
          className={`
            w-8 h-8 rounded text-sm transition-colors
             ${
               isSelected
                 ? "bg-[#009602] text-white"
                 : isToday
                 ? "bg-gray-100 text-gray-900"
                 : isDisabled
                 ? "text-gray-300 cursor-not-allowed bg-gray-50"
                 : "text-gray-700 hover:bg-gray-100"
             }
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div ref={calendarRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={`
          flex items-center justify-between w-full h-11 px-3 text-sm border rounded-md
          bg-white transition-colors
          ${
            disabled
              ? "cursor-not-allowed opacity-50 bg-gray-50 text-gray-400 border-gray-200"
              : "cursor-pointer border-gray-300 hover:border-gray-400"
          }
        `}
      >
        <span className={value ? "text-gray-900" : "text-gray-500"}>
          {value ? formatDisplayDate(value) : placeholder}
        </span>
        {/* <CalendarIcon className="h-4 w-4 text-gray-400" /> */}
        {/* custom icon */}
        <img src={Icons.calendarIcon} alt="calendar" className="h-6 w-6" />
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div
          className={`
            absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-64
            ${position === "bottom" ? "top-full mt-1" : "bottom-full mb-1"}
          `}
        >
          {/* Header with navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigateMonth("prev")}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <span className="font-medium text-sm">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>

            <button
              type="button"
              onClick={() => navigateMonth("next")}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="text-center text-xs text-gray-500 font-medium h-8 flex items-center justify-center"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">{generateCalendarDays()}</div>
        </div>
      )}
    </div>
  );
};
